import { Pencil } from "lucide-react";
import { useT } from "@/i18n";
import { fmtPrice } from "@/lib/content";

function Block({ title, onEdit, testid, rows }) {
  const { t } = useT();
  const items = rows.filter(([, v]) => v);
  return (
    <div data-testid={testid} className="rounded-xl border border-border p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{title}</p>
        <button type="button" data-testid={`${testid}-edit`} onClick={onEdit} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:text-brand-tealDark transition-colors">
          <Pencil size={11} /> {t.order.wizard.summary_edit}
        </button>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-4 gap-y-1.5 text-sm">
        {items.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground text-xs sm:text-sm">{String(k).replace(/\s*\*$/, "")}</dt>
            <dd className="font-medium break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function OrderSummary({ pkgName, billingLabel, price, total, mode, form, proxies, extra, extraFields, goTo, consent }) {
  const { t, lang } = useT();
  const O = t.order;
  const W = O.wizard;
  const modeLabel = { existing: W.mode_existing, new_jdg: W.kind_jdg, new_krs_deed: W.krs_deed, new_krs_s24: W.krs_s24 }[mode];
  const priceLabel = `${fmtPrice(price)} ${O.per_month}${total ? ` (${fmtPrice(total)} ${t.pricing.net_unit} ${O.upfront})` : ""}`;
  const legal = mode === "new_jdg" ? O.legal_forms.jdg : O.legal_forms[form.legal_form];
  return (
    <div data-testid="order-summary-step" className="space-y-4">
      <p className="text-sm font-semibold">{W.summary_title}</p>
      <Block testid="order-summary-package" title={W.steps.package} onEdit={() => goTo(0)} rows={[[O.package, pkgName], [W.summary_period, billingLabel], [W.summary_price, priceLabel]]} />
      <Block testid="order-summary-mode" title={W.steps.mode} onEdit={() => goTo(1)} rows={[[W.mode_title, modeLabel]]} />
      <Block
        testid="order-summary-details"
        title={W.steps.details}
        onEdit={() => goTo(2)}
        rows={[
          [mode === "new_jdg" ? W.person_name : O.company, form.company],
          [O.legal_form, legal],
          ["NIP", form.nip],
          ["PESEL", form.pesel],
          ["KRS", form.krs],
          [W.registered_locked, form.registered_address],
          [O.representative, mode === "new_jdg" ? "" : form.representative],
          [O.email, form.email],
          [O.phone, form.phone],
          [O.mailing_address, mode === "new_krs_s24" ? "" : [form.mailing_address, form.mailing_country].filter(Boolean).join(", ")],
          [W.summary_start, form.start_date],
          [W.summary_proxies, proxies.filter((p) => p.name.trim()).map((p) => (p.id_number ? `${p.name} (${p.id_number})` : p.name)).join("; ")],
          ...extraFields.map((f) => [lang === "pl" ? f.label : f.label_en || f.label, extra[f.key]]),
          [W.summary_notes, form.notes],
          [W.summary_consent, consent ? W.summary_consent_yes : ""],
        ]}
      />
    </div>
  );
}
